import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHelper } from '@app/_helpers';
import { AuthenticationService, VendorRequestService } from '@app/_services';
import { SubscriptionService } from '@app/_services/subscription.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ToastrService } from 'ngx-toastr';

@UntilDestroy()
@Component({
    selector: 'app-vendor-subscription-success',
    templateUrl: './vendor-subscription-success.component.html',
    styleUrls: ['./vendor-subscription-success.component.scss'],
    standalone: true,
    imports: [CommonModule, TranslateModule],
    providers: [TranslateHelper]
})
export class VendorSubscriptionSuccessComponent implements OnInit {
    vendorRequest: any = null;
    selectedPlan: any = null;
    isLoading: boolean = true;
    isCustomer: boolean = false;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        public translateHelper: TranslateHelper,
        private translateService: TranslateService,
        private vendorRequestService: VendorRequestService,
        private authService: AuthenticationService,
        private toastr: ToastrService,
        private subscriptionService: SubscriptionService
    ) {}

    ngOnInit(): void {
        this.authService.currentUser
            .pipe(untilDestroyed(this))
            .subscribe(user => {
                if (user) {
                    // Check isVendor flag (customer users have isVendor: false)
                    if (user.isVendor === false || user.type === 'customer' || user.type === 'C') {
                        this.isCustomer = true;
                        this.loadCustomerSubscription(user);
                    } else {
                        this.isCustomer = false;
                        // Load vendor request data
                        this.loadVendorRequest();
                    }
                }
            });
    }

    private loadCustomerSubscription(user: any): void {
        this.subscriptionService.checkUserSubscription(user._id).pipe(untilDestroyed(this)).subscribe({
            next: (response: any) => {
                this.isLoading = false;
                if (response.code === 200 && response.data) {
                    // Map response to match vendorRequest structure
                    this.vendorRequest = {
                        companyName: response.data.user?.name || [response.data.user?.firstName, response.data.user?.lastName].filter(Boolean).join(' ') || user.name,
                        email: response.data.user?.email || user.email,
                        planData: response.data.plan,
                        billingType: response.data.subscription?.planType || 'monthly'
                    };
                    this.selectedPlan = response.data.plan || null;
                } else {
                    this.toastr.error('Unable to load subscription details');
                }
            },
            error: (error) => {
                this.isLoading = false;
                this.toastr.error('Error loading subscription details');
            }
        });
    }

    private loadVendorRequest(): void {
        this.vendorRequestService.getMyRequest().pipe(untilDestroyed(this)).subscribe({
            next: (response: any) => {
                this.isLoading = false;
                if (response.code === 200 && response.data) {
                    this.vendorRequest = response.data;
                    this.selectedPlan = response.data.planData || null;
                } else {
                    this.toastr.error('Unable to load subscription details');
                }
            },
            error: (error) => {
                this.isLoading = false;
                console.error('Error loading vendor request:', error);
                this.toastr.error('Unable to load subscription details');
            }
        });
    }

    goToSubscriptionManagement(): void {
        const user = this.authService.currentUserValue;
        
        // If user is a vendor, go to admin
        if (user && user.isVendor) {
            this.vendorRequestService.getAdminUrl().subscribe({
                next: (response: any) => {
                    if (response.code === 200 && response.url) {
                        window.location.href = response.url;
                    } else {
                        this.toastr.error(response.msg || 'Unable to navigate to subscription management', 'Error');
                    }
                },
                error: (error) => {
                    console.error('Error getting admin URL:', error);
                    this.toastr.error('Unable to navigate to subscription management. Please ensure you are logged in.', 'Error');
                }
            });
        } else {
            // If user is a customer, go to account subscription page
            this.router.navigate(['/account/subscription']);
        }
    }

    getPlanPrice(): string {
        if (!this.selectedPlan) return '-';
        
        const billingType = this.vendorRequest?.billingType || 'monthly';
        const priceField = billingType === 'monthly' ? 'monthly_price' : 'yearly_price';
        return this.selectedPlan[priceField] || '-';
    }

    getPlanDuration(): string {
        const billingType = this.vendorRequest?.billingType || 'monthly';
        return billingType === 'monthly' ? 'monthly_billing' : 'yearly_billing';
    }
}
